import pt from 'prop-types';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../../context/locale.context';
import { MicrocopyContext } from '../../../context/microcopy.context';
import { getIfDesignAwardBadge } from '../../../helpers/badges';
import { getCopy } from '../../../helpers/copy';
import {
  cleanSearchParams,
  getActiveFilters,
  queryParamKeyMicrocopyKeyMap,
} from '../../../helpers/filters';
import useElementOnScreen from '../../../hooks/useElementOnScreen';
import useScrollPosition from '../../../hooks/useScrollPosition';
import useWindowSize from '../../../hooks/useWindowSize';
import Container from '../../../modules/container';
import { breakpoints, getCountryName } from '../../../utils/constants';
import CustomScrollable from '../customScrollable';
import Search from '../filterSearch';
import FilterSet from '../filterSet';
import WinnersFiltersModal from '../filtersModal';
import FilterTag from '../filterTag';
import Icon from '../icon';
import Image from '../image';
import Pagination from '../pagination';
import TeaserItem from '../teaserItem';
import Text from '../text';
import * as styles from './resultsList.module.scss';
import { copyToClipboard } from '/src/helpers/copyToClipboard';
import { combine } from '/src/helpers/styles';

const GOLD_AWARD_MOCK_ID = 9999;

const ResultsList = ({
  whiteBackground,
  filtersHook,
  teaserType,
  countLabelKey,
  noResultsLabelKey,
  searchTerm,
  itemRenderer: ItemRenderer,
  resultsContainerClassName,
  initialResults,
  borderInHeader,
  onTotalCountUpdated,
  noFilters,
  hideFilterOnLoad,
  onTypeChange,
  queryFilters,
  hideSort,
  filtersApiParam,
  type,
  includeSearch,
  resetSearchTerm,
  query,
  onSearchSubmit,
  isInSearchPage,
  usePagination,
  resultsPerPage = 28,
}) => {
  const [activeFilters, setActiveFilters] = useState([]); // queryFilters that match visibleFilters
  const [tempActiveFilters, setTempActiveFilters] = useState([]); // used to hold temporary filters for mobile
  const [filtersOpen, setFiltersOpen] = useState(!hideFilterOnLoad);
  const microcopy = useContext(MicrocopyContext);
  const windowSize = useWindowSize();
  const { scrollY, scrollDirection } = useScrollPosition();
  const [menuBottomY, setMenuBottomY] = useState(44);
  const [filtersBottomY, setFiltersBottomY] = useState(190);
  const [isSmallScreen, setIsSmallScreen] = useState(
    windowSize.width < breakpoints.large
  );
  const [windowLocation, setWindowLocation] = useState(null);
  const { locale } = useLocale();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const {
    filterOrderByKey = [],
    visibleFilters = [],
    openedFilters = [],
    increasePage,
    results,
    loading,
    noMoreResults,
    totalCount,
    radioFiltersKeys = [],
    mainFilter = '',
    seed,
    year = 2025,
  } = filtersHook(
    queryFilters,
    initialResults,
    filtersApiParam,
    usePagination,
    resultsPerPage
  );

  useEffect(() => {
    setWindowLocation(location);
  }, []);

  const [containerRef, isVisible] = useElementOnScreen({
    root: null,
    rootMargin: '0px',
    threshold: 0.9,
    ignore: loading || !visibleFilters,
  });

  // Notifify change on mainFilter for headline change if necessary
  useEffect(() => {
    if (mainFilter) {
      onTypeChange(mainFilter);
    }
  }, [mainFilter]);

  useEffect(() => {
    onTotalCountUpdated(totalCount);
  }, [totalCount]);

  useEffect(() => {
    let bottom;

    if (scrollDirection === 'down' && !isSmallScreen) {
      bottom = 0;
    } else {
      if (!isSmallScreen) {
        bottom = 140;
      }
    }
    if (bottom !== menuBottomY) {
      setMenuBottomY(bottom);
    }
  }, [scrollY, isSmallScreen]);

  useEffect(() => {
    let bottom;
    const desktopMenu = document.querySelector(
      '[class^="cubeNavigation-module--cubeNavigation"]'
    );
    const header = document.querySelector(
      '[class^="resultsList-module--header"]'
    );
    if (!isSmallScreen) {
      bottom = desktopMenu
        ? desktopMenu.getBoundingClientRect().bottom + header?.offsetHeight
        : 0;
    }
    if (!isSmallScreen && bottom !== filtersBottomY) {
      setFiltersBottomY(bottom);
    }
  }, [scrollY, isSmallScreen]);

  const buildSortSearchParams = ({ val, baseSearchParams = null }) => {
    let searchParams =
      baseSearchParams || new URLSearchParams(windowLocation?.search);
    searchParams.set('sort', val);

    searchParams.sort();

    return searchParams;
  };

  // calculate search params for given filter (key)
  const buildSearchParamsAfterRemove = useMemo(
    () =>
      ({ key, val, baseSearchParams = null }) => {
        let searchParams =
          baseSearchParams || new URLSearchParams(windowLocation?.search);
        const searchParamsFromKey = searchParams.getAll(key);

        searchParams.delete(key);
        if (searchParamsFromKey.length > 1) {
          for (let value of searchParamsFromKey) {
            if (String(value) !== String(val)) {
              searchParams.append(key, value);
            }
          }
        }

        searchParams.sort();

        return searchParams;
      },
    [windowLocation?.search]
  );

  const setDetailNavigationPage = (type, page) => {
    const payloadAsString = sessionStorage?.getItem(`${type}Payload`);

    if (payloadAsString) {
      const payload = JSON.parse(payloadAsString);
      payload.page = page;
      sessionStorage.setItem(`${type}Payload`, JSON.stringify(payload));
    }
  };

  const renderContributionItems = () =>
    results.map((el, index) => (
      <TeaserItem
        key={el.id}
        link={{ linkToPage: el.slug }}
        title={el.name}
        image={{ url: el.primaryMediaThumbnail, description: el.name }}
        subline={el.designation}
        year={queryFilters.time > 1 && el.awardYear}
        info={el.clients?.length && el.clients[0]}
        awardBadge={getIfDesignAwardBadge(el)}
        onLinkClick={() =>
          setDetailNavigationPage(filtersApiParam, Math.floor(index / 18))
        }
      />
    ));

  const renderProfileItems = () =>
    results.map((el, index) => (
      <TeaserItem
        key={index}
        link={{ linkToPage: el.slug }}
        title={el.companyName}
        image={{ url: el.image || el.logo, description: el.name }}
        subline={getCountryName(locale, el.country) || ''}
        imageBadge={el.image ? el.logo : null}
        imageClassName={el.image ? '' : styles.containedImage}
        onLinkClick={() =>
          setDetailNavigationPage('profile', Math.floor(index / 18))
        }
      />
    ));

  const renderCustom = () =>
    results.map((el, index) => (
      <ItemRenderer
        {...el}
        data={el}
        index={index}
        key={index}
        cappedWidth={!filtersOpen}
      />
    ));

  // TODO: refactor so all cases are handled via ItemRenderer prop
  const renderResults = () => {
    if (ItemRenderer) {
      return renderCustom();
    }

    switch (teaserType) {
      case 'design':
        return renderContributionItems();
      case 'company':
        return renderProfileItems();
      default:
        return null;
    }
  };

  useEffect(() => {
    if (windowSize.width >= breakpoints.large) {
      setIsSmallScreen(false);
    } else {
      setIsSmallScreen(true);
    }
  }, [windowSize]);

  useEffect(() => {
    if (isSmallScreen) {
      setFiltersOpen(false);
    }
  }, [isSmallScreen]);

  const renderFilters = useMemo(
    () =>
      visibleFilters
        ? filterOrderByKey
            .filter(
              (key) =>
                Array.isArray(visibleFilters[key]) && visibleFilters[key].length
            )
            .map((key) => (
              <FilterSet
                withTopBorder={!borderInHeader}
                key={key}
                label={key}
                visibleFilters={visibleFilters[key]}
                queryFilters={
                  queryFilters[key] || [queryFilters['dynamicFilterId']]
                }
                isRadio={radioFiltersKeys?.includes(key)}
                openAtStart={
                  !openedFilters.length || openedFilters.includes(key)
                }
                isDynamic={queryFilters.hasOwnProperty('dynamicFilterId')}
                type={type}
                award={filtersApiParam}
                year={year}
              />
            ))
        : null,
    [visibleFilters, queryFilters]
  );

  useEffect(() => {
    if (isVisible && !loading && !noMoreResults && !isInSearchPage) {
      increasePage();
    }
  }, [isVisible]);

  const onFilterSetChange = (filterKey, newActiveFilters) => {
    setTempActiveFilters((prevTempActiveFilters) => ({
      ...prevTempActiveFilters,
      [filterKey]: newActiveFilters,
    }));
  };

  const onFilterModalClose = () => {
    setFiltersOpen(false);
  };

  useEffect(() => {
    if (visibleFilters && queryFilters) {
      const _activeFilters = getActiveFilters(visibleFilters, queryFilters);

      // remove from query params anything that is not active
      // we always want to keep the awardId (in IFDSA it's not visible)
      cleanSearchParams({ ..._activeFilters, awardId: queryFilters.awards });

      // if dynamicFilterId (wdiFilter), find the correct label
      if (queryFilters.hasOwnProperty('dynamicFilterId')) {
        for (let filterGroup of Object.values(visibleFilters)) {
          const match = filterGroup.find(
            (filter) => filter.id === queryFilters['dynamicFilterId']
          );
          if (match) {
            setActiveFilters([{ key: 'dynamicFilterId', label: match.name }]);
          }
        }
      } else {
        setActiveFilters(
          Object.entries(_activeFilters)
            .filter(([key, val]) =>
              Array.isArray(val) ? val.length : Boolean(val)
            )
            .reduce((prev, [key, value]) => {
              if (Array.isArray(value)) {
                return prev.concat(
                  ...value.map((val) => ({
                    key,
                    id: val.id,
                    label: val.name,
                    locked: val.locked,
                  }))
                );
              }
              return prev.concat({
                key,
                label:
                  key === 'inspiration'
                    ? 'Inspire Me'
                    : key === 'profile'
                    ? value.split('|id=').shift()
                    : value,
                id: key === 'profile' ? value.split('|id=').pop() : null,
              });
            }, [])
        );
      }
    }
  }, [visibleFilters, queryFilters]);

  useEffect(() => {
    // set temporary active filters for mobile
    setTempActiveFilters(queryFilters);
  }, [queryFilters]);

  const handleShare = async () => {
    const url = new URL(location.href);
    url.searchParams.append('seed', queryFilters.seed || seed);
    url.searchParams.sort();
    const resultListLink = `${url}${location.hash ? '' : '#resultList'}`;
    if (navigator.share !== undefined) {
      navigator.share({
        title: document.title,
        text: document.title,
        url: resultListLink,
      });
    } else {
      copyToClipboard(resultListLink);
      setCopiedToClipboard(true);

      setTimeout(() => {
        setCopiedToClipboard(false);
      }, [4000]);
    }
  };

  return (
    <div data-nosnippet>
      {!noFilters && (
        <>
          <div
            className={combine(
              styles.backdrop,
              (!filtersOpen || !isSmallScreen) && styles.hidden
            )}
          ></div>
          <WinnersFiltersModal
            visibleFilters={visibleFilters}
            queryFilters={queryFilters}
            activeFilters={activeFilters}
            buildSearchParamsAfterRemove={buildSearchParamsAfterRemove}
            buildSortSearchParams={buildSortSearchParams}
            onFilterSetChange={onFilterSetChange}
            isOpen={filtersOpen && isSmallScreen}
            onClose={onFilterModalClose}
            hideSort={hideSort}
            filterOrderByKey={filterOrderByKey}
            radioFiltersKeys={radioFiltersKeys}
            type={type}
            year={year}
          />
        </>
      )}
      <div
        className={combine(
          styles.outerContainer,
          whiteBackground && styles.whiteBg,
          isInSearchPage && styles.inSearchPage,
          filtersOpen && isSmallScreen && styles.hidden
        )}
      >
        <Container
          className={combine(
            styles.headerContainer,
            whiteBackground && styles.whiteBg,
            isInSearchPage && styles.inSearchPage
          )}
        >
          {!noFilters && (
            <div
              style={{
                top: menuBottomY,
              }}
              className={combine(
                styles.header,
                borderInHeader && styles.withBorder,
                whiteBackground && styles.whiteBg,
                includeSearch && styles.withSearch,
                isInSearchPage && styles.inSearchPage,
                filtersOpen && styles.showingFilter
              )}
            >
              {includeSearch && (
                <Search
                  onSubmit={onSearchSubmit}
                  resetSearchTerm={resetSearchTerm}
                  initSearchTerm={query.find}
                  type="Jury"
                  placeholder={getCopy(
                    'placeholder.whatDoYouWantToExplore',
                    microcopy
                  )}
                  inContentPage
                  className={styles.search}
                />
              )}

              <div className={styles.actions}>
                <button
                  className={styles.button}
                  onClick={() => setFiltersOpen(!filtersOpen)}
                >
                  <Icon name="sliders-h" className={styles.icon} />
                  <Text as="span" type="infotext">
                    {filtersOpen
                      ? getCopy('results.hideFilter', microcopy)
                      : getCopy('results.showFilter', microcopy)}
                  </Text>
                </button>
                {(activeFilters.length > 0 || searchTerm) && (
                  <>
                    <Text as="span" className={styles.separator}>
                      |
                    </Text>
                    <a
                      href={windowLocation?.pathname}
                      className={styles.button}
                    >
                      <Text as="span" type="infotext" className={styles.reset}>
                        {getCopy('results.reset', microcopy)}
                      </Text>
                    </a>
                  </>
                )}
                <Text as="span" className={styles.separator}>
                  |
                </Text>
                {copiedToClipboard ? (
                  <div className={styles.copiedToClipboard}>
                    <Icon name="copy" className={styles.icon} />
                    <Text as="span" type="infotext">
                      {getCopy('results.copiedToClipboard', microcopy)}
                    </Text>
                  </div>
                ) : (
                  <button className={styles.button} onClick={handleShare}>
                    <Icon name="share-nodes" className={styles.icon} />
                    <Text as="span" type="infotext">
                      {getCopy('results.share', microcopy)}
                    </Text>
                  </button>
                )}
              </div>
              {!queryFilters.inspiration &&
                totalCount !== null &&
                !isInSearchPage && (
                  <Text
                    as="span"
                    type="metanavi"
                    variant="semibold"
                    className={styles.count}
                  >
                    {`${totalCount} ${
                      totalCount === 1
                        ? getCopy(countLabelKey, microcopy).replace(/s$/, '')
                        : getCopy(countLabelKey, microcopy)
                    }`}
                  </Text>
                )}
            </div>
          )}
          <div className={styles.main} id="resultList">
            {!noFilters && (
              <div
                style={{
                  top: filtersBottomY,
                  height: `calc(100vh - ${filtersBottomY})`,
                }}
                className={combine(
                  styles.filters,
                  filtersOpen && styles.showing
                )}
              >
                <CustomScrollable vertical className={styles.filtersScrollable}>
                  <div className={styles.activeFiltersContainer}>
                    {activeFilters
                      .filter(
                        (el) =>
                          !['categories', 'sort'].includes(el.key) && !el.locked
                      )
                      .map((filter) => {
                        const searchParams = buildSearchParamsAfterRemove({
                          key: filter.key,
                          val: filter.id,
                        }).toString();
                        return (
                          <FilterTag
                            key={`${filter.key}-${filter.id}`}
                            text={
                              queryParamKeyMicrocopyKeyMap[filter.key]
                                ? `filters.${queryParamKeyMicrocopyKeyMap[
                                    filter.key
                                  ]?.toLowerCase()}.${filter.id}`
                                : filter.label || filter.name
                            }
                            className={styles.filterTag}
                            isLocked={filter.locked}
                            isGolden={filter.id === GOLD_AWARD_MOCK_ID}
                            removeLink={`?${searchParams}`}
                            filterKey={filter.key}
                            filterId={filter.id}
                            data-test={`filtertag-${filter.key}-${filter.id}`}
                            type={type}
                            year={year}
                          />
                        );
                      })}
                  </div>
                  {renderFilters}
                </CustomScrollable>
              </div>
            )}
            {results?.length ? (
              <div className={combine(styles.w100)}>
                <div
                  style={{ '--columns': isInSearchPage ? 4 : 3 }}
                  className={combine(
                    ItemRenderer && resultsContainerClassName
                      ? resultsContainerClassName
                      : styles.results,
                    noFilters && styles.paddedResults
                  )}
                >
                  {renderResults()}
                </div>
              </div>
            ) : (
              !loading && (
                <div
                  className={combine(styles.noResults, styles.paddedResults)}
                >
                  <Text as="h3" type="h3" className={styles.text}>
                    {getCopy(noResultsLabelKey, microcopy)}
                  </Text>
                </div>
              )
            )}
          </div>
          {usePagination ? (
            <Pagination
              currentPage={queryFilters.page}
              resultsPerPage={resultsPerPage}
              totalCount={totalCount}
              siblingCount={2}
            />
          ) : (
            (!noMoreResults || queryFilters.inspiration) && (
              <div className={styles.spinnerContainer} ref={containerRef}>
                {loading ? (
                  <Image
                    image={{ url: '/images/loader.gif' }}
                    disableGatsbyImage
                    imgClassName={styles.spinner}
                  />
                ) : (
                  !queryFilters.inspiration && (
                    <Image
                      image={{ url: '/images/loader.png' }}
                      disableGatsbyImage
                      imgClassName={styles.spinner}
                    />
                  )
                )}
              </div>
            )
          )}
        </Container>
      </div>
    </div>
  );
};

export default ResultsList;

ResultsList.propTypes = {
  filtersHook: pt.func.isRequired, // see winnerFilters
  teaserType: pt.oneOf(['design', 'company']).isRequired,
  countLabelKey: pt.string,
  noResultsLabelKey: pt.string.isRequired,
  searchTerm: pt.string,
  itemRenderer: pt.func,
  resetSearchTerm: pt.func,
  initialResults: pt.array,
  whiteBackground: pt.bool,
  borderInHeader: pt.bool,
  onTotalCountUpdated: pt.func,
  hideFilterOnLoad: pt.bool,
  onTypeChange: pt.func,
  tags: pt.arrayOf(
    pt.shape({
      tag: pt.string,
      name: pt.string,
      behaviour: pt.string,
    })
  ),
  type: pt.string,
};

ResultsList.defaultProps = {
  countLabelKey: 'results.projects',
  noResultsLabelKey: 'results.generic.noResults',
  teaserType: 'design',
  searchTerm: '',
  itemRenderer: undefined,
  resetSearchTerm: () => {},
  initialResults: [],
  whiteBackground: false,
  borderInHeader: false,
  onTotalCountUpdated: () => {},
  hideFilterOnLoad: false,
  onTypeChange: null,
  tags: [],
  type: '',
};
